import { type Accessor, batch, type Component, createEffect, createMemo, createSignal, For, type JSX, onCleanup, Show } from 'solid-js'
import { twMerge } from 'tailwind-merge'
import Icon from '~/components/Icon'
import { MdiCheck, MdiInformationSlabSymbol, SolarDangerCircleLinear, SolarDangerTriangleLinear } from '~/components/icons'
import type { AlertActionType, AlertType, ConfirmOptionsType, ShowAlertOptionsType, UseAlertPropsType, UseAlertReturnType } from '~/types/useAlert'

import Button from './Button'
import Modal from './Modal'

/** Iconos por defecto según el tipo de alerta */
const defaultIcons: Record<AlertType, Component<JSX.SvgSVGAttributes<SVGSVGElement> & { size?: number | string; color?: string }> | string> = {
  success: MdiCheck,
  error: SolarDangerCircleLinear,
  info: MdiInformationSlabSymbol,
  warning: SolarDangerTriangleLinear,
  confirm: 'mdi:question-mark',
  delete: 'material-symbols-light:delete-outline',
  approve: 'material-symbols-light:verified-outline'
}

/** Colores de fondo del icono según el tipo */
const iconBgColors: Record<AlertType, string> = {
  success: 'bg-green-100 text-success dark:bg-green-900/40',
  error: 'bg-red-100 text-danger dark:bg-red-900/40',
  info: 'bg-blue-100 text-info dark:bg-blue-900/40',
  warning: 'bg-amber-100 text-warning dark:bg-amber-900/40',
  confirm: 'bg-surface-muted text-foreground-muted',
  delete: 'bg-red-100 text-danger dark:bg-red-900/40',
  approve: 'bg-green-100 text-success dark:bg-green-900/40'
}

/** Color de la barra de progreso según tipo */
const progressBarColors: Record<AlertType, string> = {
  success: 'bg-green-500',
  error: 'bg-red-500',
  info: 'bg-blue-500',
  warning: 'bg-amber-500',
  confirm: 'bg-slate-500',
  delete: 'bg-red-500',
  approve: 'bg-green-500'
}

/** Color por defecto del botón de confirmación según tipo */
const defaultConfirmColors: Record<AlertType, AlertActionType['color']> = {
  success: 'success',
  error: 'danger',
  info: 'info',
  warning: 'warning',
  confirm: 'primary',
  delete: 'danger',
  approve: 'success'
}

interface AlertContentProps {
  currentProps: Accessor<ShowAlertOptionsType>
  actions: Accessor<AlertActionType[]>
  onClose: () => void
  setIsVisible: (v: boolean) => void
  alertId: Accessor<number>
}

/** Componente interno de la alerta - optimizado */
const AlertContent = (props: AlertContentProps) => {
  const type = createMemo(() => props.currentProps().type)

  // Memoización de valores derivados para evitar re-cálculos
  const config = createMemo(() => {
    const propsValue = props.currentProps()
    const typeVal = type()

    return {
      icon: propsValue.icon || (typeVal ? defaultIcons[typeVal] : null),
      iconBg: typeVal ? iconBgColors[typeVal] : 'bg-surface-muted text-foreground-muted',
      progressColor: typeVal ? progressBarColors[typeVal] : 'bg-slate-500',
      duration: propsValue.duration
    }
  })

  // Control de la animación de la barra de progreso
  const [progressWidth, setProgressWidth] = createSignal(100)
  const [isAnimating, setIsAnimating] = createSignal(false)

  createEffect(() => {
    // Reiniciar animación cuando cambia el alertId o la duración
    const duration = config().duration
    const _id = props.alertId() // Dependencia para reiniciar

    if (duration && duration > 0) {
      // Resetear estado
      setIsAnimating(false)
      setProgressWidth(100)

      // Forzar reflow y comenzar animación
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true)
          setProgressWidth(0)
        })
      })
    }
  })

  const iconContainerClasses = createMemo(() => twMerge('w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5', config().iconBg))

  return (
    <div class="relative min-w-85 flex flex-col">
      {/* Barra de progreso optimizada con CSS transitions */}
      <Show when={config().duration && config().duration! > 0}>
        <div class="absolute top-0 left-0 right-0 h-1 bg-border rounded-t-lg overflow-hidden">
          <div
            class={twMerge('h-full ease-linear', config().progressColor, isAnimating() ? 'transition-all' : 'transition-none')}
            style={{
              width: `${progressWidth()}%`,
              'transition-duration': isAnimating() ? `${config().duration}ms` : '0ms'
            }}
          />
        </div>
      </Show>

      <div class="px-8 py-10 text-center">
        {/* Icono */}
        <Show when={config().icon}>
          <div class={iconContainerClasses()}>
            {(() => {
              const icon = config().icon
              if (typeof icon === 'string') {
                return <Icon icon={icon} size={48} />
              } else if (icon) {
                return icon({ size: 48 })
              }
              return null
            })()}
          </div>
        </Show>

        {/* Título */}
        <Show when={props.currentProps().title}>
          <h2 class="text-2xl font-bold text-foreground mb-2">{props.currentProps().title}</h2>
        </Show>

        {/* Subtítulo */}
        <Show when={props.currentProps().subtitle}>
          <h3 class="text-lg font-medium text-foreground-muted mb-3">{props.currentProps().subtitle}</h3>
        </Show>

        {/* Mensaje */}
        <Show when={props.currentProps().message}>
          <div class="text-foreground-subtle mb-6 leading-relaxed">{props.currentProps().message}</div>
        </Show>

        {/* Body personalizado */}
        <Show when={props.currentProps().body}>
          <div class="mb-6">{props.currentProps().body}</div>
        </Show>
      </div>

      {/* Acciones */}
      <Show when={props.actions().length > 0}>
        <div class="flex items-center justify-end gap-3 px-8 pb-6 pt-0 mt-auto flex-wrap">
          <For each={props.actions()}>
            {action => (
              <Button
                variant={action.variant || 'contained'}
                color={action.color || 'primary'}
                size={action.size || 'md'}
                loading={action.loading}
                disabled={action.disabled}
                startIcon={action.icon}
                onClick={async () => {
                  await action.onClick?.()
                  if (action.closeOnClick !== false) {
                    props.setIsVisible(false)
                  }
                }}
              >
                {action.label}
              </Button>
            )}
          </For>
        </div>
      </Show>
    </div>
  )
}

/**
 * Hook para mostrar alertas y diálogos de confirmación
 * Optimizado para rendimiento y limpieza de efectos
 */
const useAlert = (initialProps: UseAlertPropsType = {}): UseAlertReturnType => {
  const [isVisible, setIsVisible] = createSignal(false)
  const [currentProps, setCurrentProps] = createSignal<ShowAlertOptionsType>({
    showCloseButton: true,
    ...initialProps
  })
  const [actions, setActions] = createSignal<AlertActionType[]>([])
  const [resolvePromise, setResolvePromise] = createSignal<((value: boolean) => void) | null>(null)
  const [alertId, setAlertId] = createSignal(0)

  let timeoutId: number | undefined

  const handleClose = () => {
    if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutId = undefined
    }

    // Batch updates para evitar renders intermedios
    batch(() => {
      setIsVisible(false)
      const resolve = resolvePromise()
      if (resolve) {
        resolve(false)
        setResolvePromise(null)
      }
    })

    // Ejecutar callback después de cerrar
    currentProps().onClose?.()
  }

  /** Toggle de visibilidad */
  const toggle = (show?: boolean) => {
    if (show === true) setIsVisible(true)
    else if (show === false) handleClose()
    else setIsVisible(v => !v)
  }

  /** Mostrar alerta con opciones */
  const show = (options: ShowAlertOptionsType = {}) => {
    if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutId = undefined
    }

    batch(() => {
      setAlertId(prev => prev + 1) // Incrementar ID para forzar reset de animaciones
      setCurrentProps({
        showCloseButton: true,
        ...options
      })
      setActions(options.actions || [])
      setIsVisible(true)
    })

    // Configurar auto-cierre si hay duración
    if (options.duration && options.duration > 0) {
      timeoutId = window.setTimeout(() => {
        handleClose()
      }, options.duration)
    }
  }

  /** Limpieza al desmontar */
  onCleanup(() => {
    if (timeoutId) clearTimeout(timeoutId)
  })

  /** Métodos helpers memoizados (constantes en la instancia) */
  const helper = (type: AlertType, defaultColor: AlertActionType['color']) => (title: string, message?: string) => {
    show({
      title,
      message,
      type, // Añadimos el tipo para que AlertContent use los iconos/colores correctos
      duration: type === 'success' ? 3000 : undefined, // Success por defecto tiene autostop
      actions: [
        {
          label: type === 'error' ? 'Cerrar' : type === 'success' ? 'Aceptar' : 'Entendido',
          color: defaultColor,
          variant: 'contained'
        }
      ]
    })
  }

  const success = helper('success', 'success')
  const error = helper('error', 'danger')
  const info = helper('info', 'info')
  const warning = helper('warning', 'warning')

  /** Diálogo de confirmación genérico */
  const confirm = (options: ConfirmOptionsType): Promise<boolean> => {
    return new Promise(resolve => {
      setResolvePromise(() => resolve)

      const type = options.type || 'confirm'
      const confirmColor = options.confirmColor || defaultConfirmColors[type]

      show({
        type,
        title: options.title || '¿Estás seguro?',
        subtitle: options.subtitle,
        message: options.message,
        icon: options.icon,
        body: options.body,
        showCloseButton: false,
        actions: [
          {
            label: options.cancelText || 'Cancelar',
            variant: 'text',
            color: 'secondary',
            onClick: () => {
              resolve(false)
              setResolvePromise(null)
            },
            closeOnClick: true
          },
          {
            label: options.confirmText || 'Confirmar',
            variant: 'contained',
            color: confirmColor,
            icon: options.confirmIcon,
            onClick: () => {
              resolve(true)
              setResolvePromise(null)
            },
            closeOnClick: true
          }
        ]
      })
    })
  }

  const confirmDelete = (options: Partial<ConfirmOptionsType> = {}): Promise<boolean> => {
    return confirm({
      type: 'delete',
      title: options.title || '¿Eliminar este elemento?',
      message: options.message || 'Esta acción no se puede deshacer. El elemento será eliminado permanentemente.',
      confirmText: options.confirmText || 'Sí, eliminar',
      cancelText: options.cancelText || 'No, cancelar',
      confirmColor: 'danger',
      confirmIcon: 'material-symbols-light:delete-outline',
      ...options
    })
  }

  const confirmApprove = (options: Partial<ConfirmOptionsType> = {}): Promise<boolean> => {
    return confirm({
      type: 'approve',
      title: options.title || '¿Aprobar este elemento?',
      message: options.message || 'Una vez aprobado, esta acción entrará en vigencia inmediatamente.',
      confirmText: options.confirmText || 'Sí, aprobar',
      cancelText: options.cancelText || 'No, cancelar',
      confirmColor: 'success',
      confirmIcon: 'material-symbols-light:verified-outline',
      ...options
    })
  }

  /** Componente de alerta reactivo */
  const AlertComponent = (): JSX.Element => {
    return (
      <Modal isOpen={isVisible()} onClose={handleClose} closable size="md">
        <AlertContent currentProps={currentProps} actions={actions} onClose={handleClose} setIsVisible={setIsVisible} alertId={alertId} />
      </Modal>
    )
  }

  return {
    isVisible,
    toggle,
    show,
    close: handleClose,
    setProps: (newProps: Partial<ShowAlertOptionsType>) => {
      batch(() => {
        setCurrentProps(prev => ({ ...prev, ...newProps }))
        if (newProps.actions) setActions(newProps.actions)
      })
    },
    AlertComponent,
    success,
    error,
    info,
    warning,
    confirm,
    confirmDelete,
    confirmApprove
  }
}

export default useAlert
